/**
 * 내 계정(`/admin/settings/profile`).
 * 표시값은 로그인·앱 초기화 시 `/admin/auth/me`로 받아 authStore(persist)에
 * 저장해 둔 스냅샷을 사용한다. 프로필 진입만으로 me를 재호출하지 않는다.
 * Figma「17 내 계정 — 정보 인라인 수정」(12723:3495): 이름·연락처 인라인 편집.
 */
import {
  authService,
  type AdminMyAccountUpdateRequest,
} from '@/entities/auth/api/auth.api';
import { DataCard } from '@/shared/components/DataCard';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { ToolbarRow } from '@/shared/components/ui/row';
import { cn } from '@/shared/lib/utils';
import {
  listReadableTopLevelPermissions,
  permissionLevelLabel,
} from '@/shared/utils/authPermissions';
import {
  getRoleBadgeVariant,
  getRoleDisplayLabel,
  isFullAccessRole,
} from '@/shared/utils/authRoles';
import {
  formatKoreanMobilePhone,
  isValidKoreanMobilePhone,
  normalizePhoneDigits,
} from '@/shared/utils/phoneFormat';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { mapAdminUserToAuthUser, useAuthStore } from '@/stores/authStore';
import { Info, UserRound } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import {
  ChangePasswordModal,
  type ChangePasswordPayload,
} from './ChangePasswordModal';

type EditableField = 'name' | 'phone';

type InfoRowProps = {
  label: string;
  value: React.ReactNode;
  trailing?: React.ReactNode;
  className?: string;
};

const InfoRow: React.FC<InfoRowProps> = ({
  label,
  value,
  trailing,
  className,
}) => (
  <div
    className={cn(
      'flex items-center justify-between gap-4 px-4 py-3.5 sm:px-6',
      className,
    )}
  >
    <div className="flex min-w-0 items-center gap-0">
      <div className="w-[112px] shrink-0 text-[15px] font-medium text-muted-foreground sm:w-[180px]">
        {label}
      </div>
      <div className="truncate text-sm text-foreground">{value || '—'}</div>
    </div>
    {trailing ? <div className="shrink-0">{trailing}</div> : null}
  </div>
);

const mutedPill = (text: string) => (
  <Badge variant="secondary" className="font-normal">
    {text}
  </Badge>
);

/** 비밀번호 변경일 → Figma 안내 문구 (경과·90일 주기) */
export const formatPasswordChangedLabel = (
  passwordChangedAt: string | null | undefined,
  now = new Date(),
): string => {
  if (!passwordChangedAt) {
    return '변경 이력 없음 · 90일 주기 변경 권장';
  }
  const changed = new Date(passwordChangedAt);
  if (Number.isNaN(changed.getTime())) {
    return '변경 이력 없음 · 90일 주기 변경 권장';
  }
  const dayMs = 24 * 60 * 60 * 1000;
  const days = Math.max(
    0,
    Math.floor((now.getTime() - changed.getTime()) / dayMs),
  );
  const yyyy = changed.getFullYear();
  const mm = String(changed.getMonth() + 1).padStart(2, '0');
  const dd = String(changed.getDate()).padStart(2, '0');
  return `마지막 변경: ${yyyy}-${mm}-${dd} (${days}일 경과 · 90일 주기 변경 권장)`;
};

type InlineEditRowProps = {
  label: string;
  displayValue: string;
  editing: boolean;
  editDisabled: boolean;
  draft: string;
  hint?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  maxLength?: number;
  saving?: boolean;
  /** 변경사항이 있을 때만 저장 활성화 */
  canSave?: boolean;
  onDraftChange: (value: string) => void;
  onStartEdit: () => void;
  onSave: () => void | Promise<void>;
  onCancel: () => void;
};

const InlineEditRow: React.FC<InlineEditRowProps> = ({
  label,
  displayValue,
  editing,
  editDisabled,
  draft,
  hint,
  inputMode,
  maxLength,
  saving = false,
  canSave = true,
  onDraftChange,
  onStartEdit,
  onSave,
  onCancel,
}) => {
  if (editing) {
    return (
      <div className="flex flex-wrap items-center gap-2 bg-muted/70 px-4 py-3.5 sm:px-6">
        <div className="w-[112px] shrink-0 text-[15px] font-medium text-muted-foreground sm:w-[180px]">
          {label}
        </div>
        <Input
          value={draft}
          onChange={(e) => onDraftChange(e.target.value)}
          size="sm"
          className="w-full max-w-[320px]"
          autoFocus
          inputMode={inputMode}
          maxLength={maxLength}
          disabled={saving}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (canSave && !saving) void onSave();
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              onCancel();
            }
          }}
        />
        <Button
          type="button"
          size="sm"
          className="ruda-button"
          loading={saving}
          disabled={saving || !canSave}
          onClick={() => void onSave()}
        >
          저장
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={saving}
          onClick={onCancel}
        >
          취소
        </Button>
        {hint ? (
          <p className="text-[11px] text-muted-foreground">{hint}</p>
        ) : null}
      </div>
    );
  }

  return (
    <InfoRow
      label={label}
      value={displayValue}
      trailing={
        <Button
          type="button"
          variant="link"
          size="sm"
          className={cn(
            'h-auto px-0',
            editDisabled && 'pointer-events-none text-muted-foreground/45',
          )}
          disabled={editDisabled}
          onClick={onStartEdit}
        >
          수정
        </Button>
      }
    />
  );
};

/** me 저장값 표시용 — 빈 문자열은 대시로 */
export const displayMeValue = (value: string | null | undefined): string => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : '—';
};

export const MyAccountPage: React.FC = () => {
  const toast = useToast();
  const user = useAuthStore((s) => s.user);
  const changePassword = useAuthStore((s) => s.changePassword);

  const roleLabel = getRoleDisplayLabel(user?.role);
  const isManagerView = isFullAccessRole(user?.role);
  const isMaster = user?.role === 'ROLE_MASTER';
  /** authStore에 persist된 me.permissions 스냅샷 기준 요약 */
  const menuPermissions = listReadableTopLevelPermissions(user?.permissions);

  const [editingField, setEditingField] = useState<EditableField | null>(null);
  const [draft, setDraft] = useState('');
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  // 필드 전환 시 다른 행의 「수정」은 흐리게 (Figma)
  const otherEditing = editingField !== null;

  useEffect(() => {
    // store 사용자 스냅샷이 바뀌면 편집 중이면 취소
    setEditingField(null);
    setDraft('');
  }, [user?.email, user?.username, user?.name, user?.phone]);

  const handleChangePassword = async (payload: ChangePasswordPayload) => {
    try {
      await changePassword(payload.currentPassword, payload.newPassword);
      toast.success('비밀번호가 변경되었습니다.');
    } catch (error) {
      handleApiError(error, toast);
      throw error;
    }
  };

  const startEdit = (field: EditableField) => {
    if (editingField && editingField !== field) return;
    setEditingField(field);
    if (field === 'name') {
      setDraft(user?.name || '');
    } else {
      setDraft(formatKoreanMobilePhone(user?.phone || ''));
    }
  };

  const cancelEdit = () => {
    setEditingField(null);
    setDraft('');
  };

  const originalName = (user?.name || '').trim();
  const originalPhoneDigits = normalizePhoneDigits(user?.phone);

  const isDirty = useMemo(() => {
    if (!editingField) return false;
    if (editingField === 'name') {
      return draft.trim() !== originalName;
    }
    return normalizePhoneDigits(draft) !== originalPhoneDigits;
  }, [editingField, draft, originalName, originalPhoneDigits]);

  const canSave = useMemo(() => {
    if (!isDirty) return false;
    if (editingField === 'name') return draft.trim().length > 0;
    if (editingField === 'phone') return isValidKoreanMobilePhone(draft);
    return false;
  }, [isDirty, editingField, draft]);

  /**
   * 이름·연락처 저장 — 변경된 필드만 PATCH /admin/auth/me.
   * 성공 시 응답(또는 요청값)으로 authStore persist를 갱신한다.
   */
  const saveEdit = async () => {
    if (!user || savingProfile || !editingField || !canSave) return;

    const body: AdminMyAccountUpdateRequest = {};
    let nextName = user.name;
    let nextPhone = user.phone;

    if (editingField === 'name') {
      const name = draft.trim();
      if (!name) {
        toast.error('이름을 입력해주세요.');
        return;
      }
      body.name = name;
      nextName = name;
    }

    if (editingField === 'phone') {
      const phone = formatKoreanMobilePhone(draft).trim();
      if (!isValidKoreanMobilePhone(phone)) {
        toast.error('연락처는 010-1234-5678 형식으로 입력해주세요.');
        return;
      }
      body.phone = phone;
      nextPhone = phone;
    }

    if (Object.keys(body).length === 0) return;

    setSavingProfile(true);
    try {
      const response = await authService.updateMe(body);

      const data = response.data;
      if (data?.username != null && Array.isArray(data.roles)) {
        useAuthStore.setState({ user: mapAdminUserToAuthUser(data) });
      } else {
        useAuthStore.setState({
          user: {
            ...user,
            name: nextName,
            phone: nextPhone,
          },
        });
      }

      toast.success(
        editingField === 'name'
          ? '이름이 저장되었습니다.'
          : '연락처가 저장되었습니다.',
      );
      cancelEdit();
    } catch (error) {
      handleApiError(error, toast);
    } finally {
      setSavingProfile(false);
    }
  };

  if (!user) {
    return (
      <div className="rounded-12 border border-border bg-muted/40 px-6 py-10 text-center text-sm text-muted-foreground">
        내 정보를 불러오지 못했습니다. 다시 로그인해 주세요.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <ToolbarRow justify="between" gap={8}>
        <ToolbarRow className="gap-2.5">
          <h1 className="text-xl font-semibold tracking-tight text-foreground">
            내 정보
          </h1>
          <Badge variant={getRoleBadgeVariant(user.role)}>{roleLabel}</Badge>
        </ToolbarRow>
        <Button
          type="button"
          variant="outline"
          onClick={() => setChangePasswordOpen(true)}
        >
          비밀번호 변경
        </Button>
      </ToolbarRow>

      <DataCard className="py-2">
        <InfoRow
          label="계정 ID (이메일)"
          value={displayMeValue(user.email)}
          trailing={
            isManagerView ? mutedPill('변경 불가') : mutedPill('관리자 문의')
          }
        />

        <InfoRow
          label="유저네임"
          value={displayMeValue(user.username)}
          trailing={mutedPill('변경 불가')}
        />

        <InlineEditRow
          label="이름"
          displayValue={displayMeValue(user.name)}
          editing={editingField === 'name'}
          editDisabled={otherEditing && editingField !== 'name'}
          draft={draft}
          saving={savingProfile && editingField === 'name'}
          canSave={editingField === 'name' && canSave}
          onDraftChange={setDraft}
          onStartEdit={() => startEdit('name')}
          onSave={saveEdit}
          onCancel={cancelEdit}
        />

        <InlineEditRow
          label="연락처"
          displayValue={
            user.phone
              ? formatKoreanMobilePhone(user.phone)
              : displayMeValue(user.phone)
          }
          editing={editingField === 'phone'}
          editDisabled={otherEditing && editingField !== 'phone'}
          draft={draft}
          saving={savingProfile && editingField === 'phone'}
          canSave={editingField === 'phone' && canSave}
          hint="숫자 입력 시 010-1234-5678 형식으로 자동 변환됩니다"
          inputMode="tel"
          maxLength={13}
          onDraftChange={(value) => setDraft(formatKoreanMobilePhone(value))}
          onStartEdit={() => startEdit('phone')}
          onSave={saveEdit}
          onCancel={cancelEdit}
        />

        <InfoRow
          label="소속"
          value={displayMeValue(user.department)}
          trailing={isManagerView ? undefined : mutedPill('관리자 문의')}
        />
        <InfoRow
          label="역할"
          value={roleLabel || '—'}
          trailing={
            isMaster
              ? mutedPill('역할 변경 불가 · 시스템 유일 계정')
              : isManagerView
                ? mutedPill('역할 변경 불가')
                : mutedPill('관리자 문의')
          }
        />
        <InfoRow
          label="비밀번호"
          value={formatPasswordChangedLabel(user.passwordChangedAt)}
          trailing={isManagerView ? undefined : mutedPill('관리자 문의')}
        />
      </DataCard>

      {isManagerView ? (
        <div className="flex items-start gap-2 rounded-12 bg-ruda-info-50 px-3.5 py-2.5 text-sm text-ruda-info">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <p>
            {isMaster
              ? '마스터 계정은 전체 메뉴 및 모든 계정 관리 권한을 가집니다. 마스터 정보는 본인만 수정할 수 있습니다.'
              : '관리 계정은 부여된 전체 메뉴 권한으로 운영할 수 있습니다. 계정 정보는 본인만 수정할 수 있습니다.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <h2 className="text-sm font-bold text-foreground">
            내 메뉴 권한 (읽기 전용)
          </h2>
          <DataCard>
            {menuPermissions.length === 0 ? (
              <div className="flex items-center gap-2 px-6 py-8 text-sm text-muted-foreground">
                <UserRound className="h-4 w-4" aria-hidden />
                표시할 메뉴 권한이 없습니다.
              </div>
            ) : (
              menuPermissions.map((item, index) => (
                <div
                  key={item.menuCode}
                  className={cn(
                    'flex items-center justify-between px-6 py-3',
                    index > 0 && 'border-t border-border',
                  )}
                >
                  <span className="text-[13px] text-foreground">
                    {item.label}
                  </span>
                  <Badge
                    variant={item.level === 'WRITE' ? 'success' : 'secondary'}
                  >
                    {permissionLevelLabel(item.level)}
                  </Badge>
                </div>
              ))
            )}
          </DataCard>
          <p className="text-xs text-muted-foreground">
            메뉴 권한 변경이 필요한 경우 관리자에게 요청하세요.
          </p>
        </div>
      )}

      <ChangePasswordModal
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
        onSubmit={handleChangePassword}
      />
    </div>
  );
};
